import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { instance } from "@semo/api-contracts";
import type { CompanyProfileData } from "@semo/shared-ui";

/** 백엔드 MySupplierProfileDto — 공개 프로필 + 완성도 */
export type MyCompanyProfile = CompanyProfileData & {
  completeness: { percent: number; missing: string[] };
};

export interface UpdateMyCompanyProfileInput {
  homepageUrl?: string;
  establishedAt?: string;
  companyIntro?: string;
  productDescription?: string;
}

export interface AddCertificationInput {
  name: string;
  issuer?: string;
  certNumber?: string;
  issuedAt?: string;
  expiresAt?: string;
  file?: File | null;
}

const QUERY_KEY = ["my-company-profile"];

// ⚠️ Authorization 헤더를 직접 만들지 않는다 — 임베드 로그인(useEmbedAuth→setAuthToken)은
// 토큰을 axios 인스턴스 기본 헤더에만 심고 localStorage 에는 쓰지 않는다. localStorage 에서
// 읽어 헤더를 만들면 `Bearer null` 이 기본 헤더를 덮어써 전 요청이 401 이 된다(스테이징 실측).
// 백엔드는 전 응답을 { success, data } 로 감싼다(ResponseValidationInterceptor).
type Envelope<T> = { data: T };

async function fetchMyProfile(): Promise<MyCompanyProfile> {
  const response = await instance.get<Envelope<MyCompanyProfile>>(
    "/api/v1/suppliers/me/profile",
  );
  return response.data.data;
}

export function useMyCompanyProfile(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchMyProfile,
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60,
    retry: false,
  });
}

export function useUpdateMyCompanyProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateMyCompanyProfileInput) => {
      const response = await instance.patch<Envelope<MyCompanyProfile>>(
        "/api/v1/suppliers/me/profile",
        input,
      );
      return response.data.data;
    },
    onSuccess: (profile) => {
      queryClient.setQueryData(QUERY_KEY, profile);
    },
  });
}

export function useUploadCompanyLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const response = await instance.post<Envelope<{ logoUrl: string }>>(
        "/api/v1/suppliers/me/logo",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useAddCertification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddCertificationInput) => {
      const formData = new FormData();
      formData.append("name", input.name);
      if (input.issuer) formData.append("issuer", input.issuer);
      if (input.certNumber) formData.append("certNumber", input.certNumber);
      if (input.issuedAt) formData.append("issuedAt", input.issuedAt);
      if (input.expiresAt) formData.append("expiresAt", input.expiresAt);
      if (input.file) formData.append("file", input.file);

      const response = await instance.post<Envelope<unknown>>(
        "/api/v1/suppliers/me/certifications",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useDeleteCertification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (certificationId: string) => {
      await instance.delete(
        `/api/v1/suppliers/me/certifications/${certificationId}`,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
